import { AppState } from '../../../app/types'
import { GroupId } from '../../../config/systemConfig'
import { getWeakestFamiliesGlobal } from '../../../mastery'
import { FAMILY_DISPLAY_NAMES } from './familyDisplayNames'
import { getFamilyDefinition, MODULES } from '../../../content'

export interface DifferentiationInsight {
  familyId: string
  familyName: string
  moduleId: string
  moduleName: string
  mastery: number
  primaryErrorType?: string
  recommendation: string
}

const ERROR_TYPE_RECOMMENDATIONS: Record<string, string> = {
  // Clock errors
  HOUR_OFF:
    'Verwart vaak het uur (bijv. 3 uur i.p.v. 4 uur). Oefen met de kleine wijzer.',
  MINUTE_OFF_5:
    'Zit er vaak 5 minuten naast. Oefen met de sprongen van 5 op de klok.',
  MINUTE_OFF_10:
    'Zit er vaak 10 minuten naast. Oefen met de sprongen van 5 op de klok.',
  MINUTE_OFF_15: 'Verwart kwart voor/over. Oefen specifiek met kwartieren.',
  HALF_CONFUSION:
    'Verwart half met het hele uur. Leg uit dat "half 4" betekent: op de helft naar 4 uur.',
  FORMAT_24H: 'Verwart 12-uurs en 24-uurs notatie (bijv. 15:00 i.p.v. 03:00).',
  BOTH_OFF: 'Heeft moeite met zowel uren als minuten aflezen.',

  // Money errors
  TOO_LOW: 'Maakt het bedrag vaak te laag. Oefen met doortellen.',
  TOO_HIGH: 'Maakt het bedrag vaak te hoog. Oefen met gepast betalen.',
  TOO_LOW_5C: 'Vergeet vaak 5 cent toe te voegen.',
  TOO_HIGH_5C: 'Voegt vaak 5 cent te veel toe.',
  TOO_LOW_10C: 'Vergeet vaak 10 cent toe te voegen.',
  TOO_HIGH_10C: 'Voegt vaak 10 cent te veel toe.',
  TOO_LOW_50C: 'Vergeet vaak 50 cent toe te voegen.',
  TOO_HIGH_50C: 'Voegt vaak 50 cent te veel toe.',
  TOO_LOW_100C: 'Vergeet vaak een hele euro.',
  TOO_HIGH_100C: 'Voegt vaak een euro te veel toe.',
  INVALID_DENOM:
    'Gebruikt munten/biljetten die niet zijn toegestaan in de opgave.',
  TOO_FEW_TOKENS: 'Gebruikt te weinig munten/biljetten.',
  TOO_MANY_TOKENS:
    'Gebruikt te veel munten/biljetten. Oefen met zo min mogelijk munten betalen.',
  MISSING_REQUIRED_BILL: 'Vergeet het verplichte biljet te gebruiken.',

  // Spelling/Grammar (hypothetical based on feature description)
  STAM_T: 'Verwart stam+t met voltooid deelwoord.',
  D_T: 'Heeft moeite met d/t aan het eind van een woord.',
  EI_IJ: 'Verwart ei en ij. Oefen met het ei-verhaal.',
  AU_OU: 'Verwart au en ou. Oefen met het au-verhaal.',
}

export function getStudentInsights(
  studentData: AppState,
  group: GroupId,
  limit: number = 3,
): DifferentiationInsight[] {
  if (!studentData.familyMastery) return []

  // Get weakest families (mastery < 40, attempted > 0)
  const weakestFamilies = getWeakestFamiliesGlobal(
    studentData.familyMastery,
    group,
    limit,
  )

  return weakestFamilies.map((state) => {
    const familyId = state.familyId
    const familyName = FAMILY_DISPLAY_NAMES[familyId] || familyId
    const def = getFamilyDefinition(familyId)
    const moduleId = def?.module || 'ONBEKEND'
    const moduleDef = MODULES.find((m) => m.id === moduleId)
    const moduleName = moduleDef?.title || moduleId

    // Analyze error patterns from history
    let primaryErrorType: string | undefined
    let recommendation = 'Heeft extra instructie nodig bij dit onderwerp.'

    if (state.history && state.history.length > 0) {
      // Count error types in recent history
      const errorCounts: Record<string, number> = {}
      let totalErrors = 0

      state.history.forEach((attempt) => {
        if (!attempt.isCorrect && attempt.errorType) {
          errorCounts[attempt.errorType] =
            (errorCounts[attempt.errorType] || 0) + 1
          totalErrors++
        }
      })

      // Find most common error type if there are enough errors to form a pattern
      if (totalErrors >= 2) {
        let maxCount = 0
        let mostCommonError = ''

        Object.entries(errorCounts).forEach(([errorType, count]) => {
          if (count > maxCount) {
            maxCount = count
            mostCommonError = errorType
          }
        })

        if (mostCommonError && maxCount >= 2) {
          primaryErrorType = mostCommonError
          // Clean up error type for matching (e.g. INVALID_DENOM_50 -> INVALID_DENOM)
          const baseErrorType = mostCommonError.replace(/_\d+$/, '')

          if (ERROR_TYPE_RECOMMENDATIONS[mostCommonError]) {
            recommendation = ERROR_TYPE_RECOMMENDATIONS[mostCommonError]
          } else if (ERROR_TYPE_RECOMMENDATIONS[baseErrorType]) {
            recommendation = ERROR_TYPE_RECOMMENDATIONS[baseErrorType]
          } else {
            recommendation = `Maakt vaak een specifieke fout (${mostCommonError}). Bekijk de antwoorden samen.`
          }
        }
      }
    }

    return {
      familyId,
      familyName,
      moduleId,
      moduleName,
      mastery: state.masteryScore,
      primaryErrorType,
      recommendation,
    }
  })
}
